import { me } from './session.ts';

/**
 * Each agency's peripheral modules (#285, ADR-0029): the administration of Nubi turns one off for
 * an agency, and it leaves that agency's menu (its API answers 403 MODULE_OFF). The console reads
 * the ones turned off in the profile (`modulesOff`); everything is on when it says nothing. The
 * modules that move money (Pólizas, Caja, Comisiones, Liquidaciones, Remesas) are not here: they
 * are always on.
 */

export type Module =
  | 'ANALYTICS'
  | 'GOALS'
  | 'LEADS'
  | 'CARRIER_INTEGRATION'
  | 'KNOWLEDGE'
  | 'ONBOARDING'
  | 'BACKGROUND_CHECKS'
  | 'COURSES'
  | 'APPLICATION_FORM'
  | 'REMINDERS';

/** Each module as the agency's menu names it, its screen, and what turning it off takes away. */
export const MODULES: { module: Module; label: string; view: string; covers: string }[] = [
  {
    module: 'ANALYTICS',
    label: 'Estadísticas',
    view: 'estadisticas',
    covers: 'Los indicadores de la cartera por mes, aseguradora y agente.',
  },
  {
    module: 'GOALS',
    label: 'Metas',
    view: 'metas',
    covers: 'Las metas e incentivos de la agencia y de cada agente.',
  },
  {
    module: 'LEADS',
    label: 'Prospectos',
    view: 'prospectos',
    covers: 'El embudo de prospectos y su seguimiento.',
  },
  {
    module: 'CARRIER_INTEGRATION',
    label: 'Integración con G&T',
    view: 'integracion-gyt',
    covers: 'El estado de la integración con Seguros G&T y sus llamadas.',
  },
  {
    module: 'KNOWLEDGE',
    label: 'Conocimiento',
    view: 'conocimiento',
    covers: 'Los documentos de las aseguradoras y sus preguntas.',
  },
  {
    module: 'ONBOARDING',
    label: 'Onboarding',
    view: 'onboarding',
    covers: 'Los candidatos a agente, sus pasos y sus cursos asignados.',
  },
  {
    module: 'BACKGROUND_CHECKS',
    label: 'Antecedentes',
    view: 'antecedentes',
    covers: 'La revisión de antecedentes de los candidatos.',
  },
  {
    module: 'COURSES',
    label: 'Cursos',
    view: 'cursos',
    covers: 'Crear y editar cursos y exámenes (Onboarding los sigue asignando).',
  },
  {
    module: 'APPLICATION_FORM',
    label: 'Boleta de onboarding',
    view: 'boleta',
    covers: 'Editar la boleta que llenan los candidatos.',
  },
  {
    module: 'REMINDERS',
    label: 'Recordatorios',
    view: 'recordatorios',
    covers: 'Configurar los recordatorios y ver sus trabajos (los envíos automáticos siguen).',
  },
];

/** A module's name in the person's words (its code when unknown). */
export const moduleLabel = (m: string) => MODULES.find((x) => x.module === m)?.label ?? m;

/** Whether a module is on for the signed-in person's agency (on when the profile says nothing). */
export const moduleOn = (m: Module) => !(me?.modulesOff ?? []).includes(m);

/** Whether a screen is on: a screen of no module (money, shared, the platform's) always is. */
export const viewOn = (view: string) => {
  const found = MODULES.find((x) => x.view === view);
  return !found || moduleOn(found.module);
};
